import { useEffect, useState } from "react";
import { useRenterDashboardContext } from "../renter-dashboard/useRenterDashboardContext";
import { getRenterInstallment, syncInstallmentsAsaas } from "@/common/services/payments.api";
import { formatDate } from "@/common/helpers/date.helpers";
import { formatCurrency } from "@/common/helpers/mask.helpers";
import { CalendarIcon, ChevronDownIcon, ClockIcon, DollarSignIcon, CreditCardIcon, CopyIcon, ExternalLinkIcon } from "lucide-react";
import { Disclosure } from "@headlessui/react";
import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { IInstallment } from "@/types/installment.type";

export default function RenterDashboardInstallment() {
  const { notifySuccess, notifyError } = useToastContext();
  const { renter } = useRenterDashboardContext();
  const [installments, setInstallments] = useState<IInstallment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSync, setIsLoadingSync] = useState(false);

  const renterId = renter._id;

  const handleSyncAsaasInstallments = async (renterId: string) => {
    try {
      setIsLoadingSync(true);
      const result = await syncInstallmentsAsaas(renterId);
      const { ok } = result;

      if (ok) {
        notifySuccess("Parcelas sincronizadas com sucesso");
        await loadInstallmentsRenter(renterId);
      } else {
        notifyError("Erro ao sincronizar parcelas");
      }
    } catch (error) {
      console.error("Erro ao sincronizar parcelas:", error);
      notifyError("Erro ao sincronizar parcelas");
    } finally {
      setIsLoadingSync(false);
    }
  };

  const loadInstallmentsRenter = async (renterId: string) => {
    if (!renterId) return;

    setIsLoading(true);
    try {
      const result = await getRenterInstallment(renterId);
      const { ok, data } = result;

      if (ok && data) {
        setInstallments(data);
      }
    } catch (error) {
      console.error("Erro ao carregar parcelas:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (renterId) {
      loadInstallmentsRenter(renterId);
    }
  }, [renterId]);

  const getPaymentStatusColor = (paymentDate: string) => {
    const isPaid = paymentDate && paymentDate !== '';
    return isPaid
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-red-100 text-red-800 border-red-200';
  };

  const getBillingTypeLabel = (type: string) => {
    switch (type.toUpperCase()) {
      case 'CREDIT_CARD':
        return 'Cartão de Crédito';
      case 'BOLETO':
        return 'Boleto';
      case 'PIX':
        return 'PIX';
      case 'DEBIT_CARD':
        return 'Cartão de Débito';
      default:
        return type;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!installments.length) {
    return (
      <div className="bg-white rounded-lg shadow-md justify-center p-6">

        <div className="text-center text-gray-500">
          <h3 className="text-lg font-medium mb-2">Nenhum parcelamento encontrado</h3>
          <p>Este cliente não possui parcelamentos ativas no momento.</p>
        </div>
        <div className="flex justify-center mt-4">
          <ButtonWithLoader
            onClick={() => handleSyncAsaasInstallments(renterId!)}
            isLoading={isLoadingSync}
          >
            Sincronizar com Asaas
          </ButtonWithLoader>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Parcelamentos</h1>
        <ButtonWithLoader
          onClick={() => handleSyncAsaasInstallments(renterId!)}
          isLoading={isLoadingSync}
        >
          Sincronizar com Asaas
        </ButtonWithLoader>

      </div>

      {/* Lista de parcelas */}
      <div className="space-y-4">
        {installments.map((installment) => (
          <div key={installment._id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Cabeçalho da parcela */}
            <div className={`p-4 border-b ${installment.paymentDate ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Parcela #{installment.installmentNumberId}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getPaymentStatusColor(installment.paymentDate)}`}>
                      {installment.paymentDate ? 'Paga' : 'Pendente'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{installment.description}</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">
                    {formatCurrency(installment.value)}
                  </div>
                  <div className="text-sm text-gray-500">
                    Líquido: {formatCurrency(installment.netValue)}
                  </div>
                </div>
              </div>
            </div>

            {/* Cards de informações */}
            <div className="p-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
              <InfoCard
                label="Forma de pagamento"
                value={getBillingTypeLabel(installment.billingType)}
                icon={<CreditCardIcon className="h-4 w-4" />}
              />
              <InfoCard
                label="Data de criação"
                value={formatDate(installment.dateCreated)}
                icon={<ClockIcon className="h-4 w-4" />}
              />
              <InfoCard
                label="Dia do vencimento"
                value={`Dia ${installment.expirationDay}`}
                icon={<CalendarIcon className="h-4 w-4" />}
              />
              <InfoCard
                label="Valor pago"
                value={formatCurrency(installment.paymentValue || 0)}
                icon={<DollarSignIcon className="h-4 w-4" />}
              />
            </div>

            {/* Seção expansível para detalhes */}
            <Disclosure>
              {({ open }) => (
                <>
                  <Disclosure.Button className="w-full px-4 py-3 bg-gray-50 text-sm font-medium text-gray-700 flex justify-between items-center hover:bg-gray-100 transition-colors">
                    <span>Detalhes completos</span>
                    <ChevronDownIcon className={`${open ? 'transform rotate-180' : ''} h-5 w-5 transition-transform`} />
                  </Disclosure.Button>
                  <Disclosure.Panel className="px-4 py-4 bg-gray-50 border-t space-y-4">
                    {/* Data de pagamento */}
                    {installment.paymentDate && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Informações de Pagamento</h4>
                        <div className="bg-white p-3 rounded-lg">
                          <p className="text-sm text-gray-600">
                            Pago em: <span className="font-medium">{formatDate(installment.paymentDate)}</span>
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Links e URLs */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {installment.paymentLink && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Link de Pagamento</h4>
                          <div className="flex items-center justify-between bg-white p-3 rounded-lg border">
                            <span className="text-xs text-gray-500 truncate pr-2">Link disponível</span>
                            <a
                              href={installment.paymentLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 flex items-center"
                            >
                              <ExternalLinkIcon className="h-4 w-4" />
                            </a>
                          </div>
                        </div>
                      )}

                      {installment.transactionReceiptUrl && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Comprovante</h4>
                          <div className="flex items-center justify-between bg-white p-3 rounded-lg border">
                            <span className="text-xs text-gray-500 truncate pr-2">Comprovante disponível</span>
                            <a
                              href={installment.transactionReceiptUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 flex items-center"
                            >
                              <ExternalLinkIcon className="h-4 w-4" />
                            </a>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Reembolsos */}
                    {installment.refunds && installment.refunds.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Reembolsos</h4>
                        <div className="space-y-2">
                          {installment.refunds.map((refund, index) => (
                            <div key={index} className="bg-white p-3 rounded-lg border">
                              <div className="flex justify-between items-start mb-2">
                                <span className="text-sm font-medium">{formatCurrency(refund.value)}</span>
                                <span className={`px-2 py-1 rounded-full text-xs ${refund.status === 'DONE' ? 'bg-green-100 text-green-800' :
                                  refund.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-red-100 text-red-800'
                                  }`}>
                                  {refund.status}
                                </span>
                              </div>
                              <p className="text-xs text-gray-600">{refund.description}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                Criado em: {formatDate(refund.dateCreated)}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* IDs técnicos */}
                    <div className="space-y-2">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-1">ID da Parcela</h4>
                        <CopyableText text={installment.installmentNumberId} />
                      </div>
                      {installment.customer && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-1">ID do Cliente</h4>
                          <CopyableText text={installment.customer} />
                        </div>
                      )}
                    </div>
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          </div>
        ))}
      </div>
    </div>
  );
}

// Componente auxiliar para informações
const InfoCard = ({ label, value, icon }: { label: string, value: string, icon?: React.ReactNode }) => (
  <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
    <div className="flex items-center space-x-2 text-gray-500 mb-1">
      {icon}
      <span className="text-xs font-medium">{label}</span>
    </div>
    <div className="font-medium text-gray-900 text-sm">{value}</div>
  </div>
);

// Componente para texto copiável
const CopyableText = ({ text }: { text: string }) => (
  <div className="flex items-center justify-between bg-white p-2 rounded border text-xs font-mono">
    <span className="truncate pr-2">{text}</span>
    <button
      onClick={() => navigator.clipboard.writeText(text)}
      className="text-blue-600 hover:text-blue-800 transition-colors"
      title="Copiar"
    >
      <CopyIcon className="h-4 w-4" />
    </button>
  </div>
);